import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, MapPin, Clock, Check, Crown, Ticket, MessageSquare, History, Users, Bell, Settings, User, Lock, Save } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

// Notification Settings Component
const NotificationSettings = ({ token }) => {
  const [prefs, setPrefs] = useState({
    notify_new_events: true,
    notify_new_posts: true,
    notify_new_comments: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const response = await axios.get(`${API}/account/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPrefs(response.data);
      } catch (error) {
        console.error("Failed to fetch notification preferences:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPrefs();
  }, [token]);

  const updatePref = async (key, value) => {
    const newPrefs = { ...prefs, [key]: value };
    setPrefs(newPrefs);
    setSaving(true);
    
    try {
      await axios.put(`${API}/account/notifications`, newPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Preferences saved");
    } catch (error) {
      toast.error("Failed to save preferences");
      setPrefs(prefs); // Revert on error
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="border border-white/10 rounded-lg bg-obsidian-light p-6" data-testid="notification-settings">
      <div className="flex items-center gap-3 mb-6">
        <Bell className="w-6 h-6 text-electric" />
        <h3 className="font-display text-xl text-white">Notification Preferences</h3>
      </div>
      
      <p className="text-white/50 mb-6">
        Choose which notifications you'd like to receive when you have push notifications enabled.
      </p>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-obsidian rounded-lg">
          <div>
            <p className="text-white font-medium">New Events</p>
            <p className="text-white/50 text-sm">Get notified when new events are posted</p>
          </div>
          <Switch 
            checked={prefs.notify_new_events}
            onCheckedChange={(checked) => updatePref('notify_new_events', checked)}
            disabled={saving}
            data-testid="toggle-new-events"
          />
        </div>
        
        <div className="flex items-center justify-between p-4 bg-obsidian rounded-lg">
          <div>
            <p className="text-white font-medium">New Posts</p>
            <p className="text-white/50 text-sm">Get notified when someone posts on an event you've RSVP'd to</p>
          </div>
          <Switch 
            checked={prefs.notify_new_posts}
            onCheckedChange={(checked) => updatePref('notify_new_posts', checked)}
            disabled={saving}
            data-testid="toggle-new-posts"
          />
        </div>
        
        <div className="flex items-center justify-between p-4 bg-obsidian rounded-lg">
          <div>
            <p className="text-white font-medium">New Comments</p>
            <p className="text-white/50 text-sm">Get notified when someone comments on a post</p>
          </div>
          <Switch 
            checked={prefs.notify_new_comments}
            onCheckedChange={(checked) => updatePref('notify_new_comments', checked)}
            disabled={saving}
            data-testid="toggle-new-comments"
          />
        </div>
      </div>
    </div>
  );
};

// Account Settings Component
const AccountSettings = ({ token, user, refreshUser }) => {
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || ""
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    
    try {
      await axios.put(`${API}/account/profile`, {
        name: profileData.name,
        email: profileData.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Profile updated successfully!");
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords don't match");
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setSavingPassword(true);
    
    try {
      await axios.put(`${API}/account/password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Password updated successfully!");
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update password");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Settings */}
      <div className="border border-white/10 rounded-lg bg-obsidian-light p-4 sm:p-6" data-testid="profile-settings">
        <div className="flex items-center gap-3 mb-4 sm:mb-6">
          <User className="w-5 h-5 sm:w-6 sm:h-6 text-electric" />
          <h3 className="font-display text-lg sm:text-xl text-white">Profile Settings</h3>
        </div>
        
        <form onSubmit={handleProfileUpdate} className="space-y-4">
          <div>
            <label className="block text-white/70 text-sm mb-2">Full Name</label>
            <Input
              type="text"
              value={profileData.name}
              onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
              className="bg-obsidian border-white/10 text-white"
              placeholder="Your name"
            />
          </div>
          
          <div>
            <label className="block text-white/70 text-sm mb-2">Email Address</label>
            <Input
              type="email"
              value={profileData.email}
              onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
              className="bg-obsidian border-white/10 text-white"
              placeholder="your@email.com"
            />
          </div>
          
          <Button
            type="submit"
            disabled={savingProfile}
            className="bg-electric text-obsidian hover:bg-electric-light gap-2 w-full sm:w-auto"
          >
            {savingProfile ? (
              <div className="w-4 h-4 border-2 border-obsidian border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Profile
          </Button>
        </form>
      </div>

      {/* Password Settings */}
      <div className="border border-white/10 rounded-lg bg-obsidian-light p-4 sm:p-6" data-testid="password-settings">
        <div className="flex items-center gap-3 mb-4 sm:mb-6">
          <Lock className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400" />
          <h3 className="font-display text-lg sm:text-xl text-white">Change Password</h3>
        </div>
        
        <form onSubmit={handlePasswordUpdate} className="space-y-4">
          <div>
            <label className="block text-white/70 text-sm mb-2">Current Password</label>
            <Input
              type="password"
              value={passwordData.current_password}
              onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
              className="bg-obsidian border-white/10 text-white"
              placeholder="Enter current password"
              required
            />
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-white/70 text-sm mb-2">New Password</label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                className="bg-obsidian border-white/10 text-white"
                placeholder="New password"
                required
                minLength={6}
              />
            </div>
            
            <div>
              <label className="block text-white/70 text-sm mb-2">Confirm Password</label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                className="bg-obsidian border-white/10 text-white"
                placeholder="Confirm password"
                required
                minLength={6}
              />
            </div>
          </div>
          
          <Button
            type="submit"
            disabled={savingPassword}
            className="bg-amber-500 text-obsidian hover:bg-amber-400 gap-2 w-full sm:w-auto"
          >
            {savingPassword ? (
              <div className="w-4 h-4 border-2 border-obsidian border-t-transparent rounded-full animate-spin" />
            ) : (
              <Lock className="w-4 h-4" />
            )}
            Update Password
          </Button>
        </form>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, token, refreshUser } = useAuth();
  const [myEvents, setMyEvents] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const [eventsRes, postsRes] = await Promise.all([
        axios.get(`${API}/my-events`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/user/posts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setMyEvents(eventsRes.data);
      setPosts(postsRes.data);
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    } finally {
      setLoading(false);
    }
  };

  const upcomingEvents = myEvents.filter(e => !e.is_past);
  const pastEvents = myEvents.filter(e => e.is_past);

  return (
    <div className="min-h-screen bg-obsidian pt-20" data-testid="dashboard-page">
      {/* Header */}
      <section className="py-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <h1 className="font-display text-3xl sm:text-4xl text-white mb-2">
                Welcome, {user?.name}
              </h1>
              <p className="text-white/60">
                Manage your RSVPs, posts, and membership all in one place.
              </p>
            </div>
            
            {user?.is_subscriber ? (
              <div className="flex items-center gap-3 bg-electric/10 border border-electric/30 rounded-lg px-4 py-3">
                <Crown className="w-5 h-5 text-electric" />
                <div>
                  <p className="text-electric text-sm font-medium uppercase">{user.subscription_tier || 'Active'} Member</p>
                  <p className="text-white/50 text-xs">Active subscription</p>
                </div>
              </div>
            ) : (
              <Link to="/membership">
                <Button className="bg-electric text-obsidian hover:bg-electric-light">
                  Become a Member
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : (
            <Tabs defaultValue="upcoming" className="space-y-6">
              <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                <TabsList className="bg-obsidian-light border border-white/10 p-1 inline-flex min-w-max">
                  <TabsTrigger 
                    value="upcoming" 
                    className="data-[state=active]:bg-electric data-[state=active]:text-obsidian text-xs sm:text-sm whitespace-nowrap"
                    data-testid="tab-upcoming"
                  >
                    <Ticket className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Upcoming</span>
                    <span className="sm:hidden">Up</span> ({upcomingEvents.length})
                  </TabsTrigger>
                  <TabsTrigger 
                    value="past"
                    className="data-[state=active]:bg-electric data-[state=active]:text-obsidian text-xs sm:text-sm whitespace-nowrap"
                    data-testid="tab-past"
                  >
                    <History className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Past Events</span>
                    <span className="sm:hidden">Past</span> ({pastEvents.length})
                  </TabsTrigger>
                  <TabsTrigger 
                    value="posts"
                    className="data-[state=active]:bg-electric data-[state=active]:text-obsidian text-xs sm:text-sm whitespace-nowrap"
                    data-testid="tab-posts"
                  >
                    <MessageSquare className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">My Posts</span>
                    <span className="sm:hidden">Posts</span> ({posts.length})
                  </TabsTrigger>
                  <TabsTrigger 
                    value="settings"
                    className="data-[state=active]:bg-electric data-[state=active]:text-obsidian text-xs sm:text-sm whitespace-nowrap"
                    data-testid="tab-settings"
                  >
                    <Settings className="w-4 h-4 mr-1 sm:mr-2" />
                    Settings
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Upcoming Events Tab */}
              <TabsContent value="upcoming" className="space-y-4">
                {upcomingEvents.length === 0 ? (
                  <div className="text-center py-16 border border-white/10 rounded-lg bg-obsidian-light">
                    <Calendar className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50 mb-4">No upcoming RSVPs</p>
                    <Link to="/events">
                      <Button variant="outline" className="border-electric text-electric hover:bg-electric/10">
                        Browse Events
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {upcomingEvents.map((event) => (
                      <Link 
                        to={`/events/${event.id}`}
                        key={event.id}
                        className="block p-6 border border-white/10 rounded-lg bg-obsidian-light hover:border-electric/50 transition-colors"
                        data-testid={`event-${event.id}`}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-display text-xl text-white mb-1">{event.title}</h3>
                            <p className="text-electric text-sm">{event.performer}</p>
                          </div>
                          <span className="flex items-center gap-1 text-electric text-sm bg-electric/10 px-2 py-1 rounded">
                            <Check className="w-4 h-4" />
                            RSVPed
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-white/60">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {event.date?.startsWith("Every") ? event.date : new Date(event.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {event.time}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Past Events Tab - For Community Sharing */}
              <TabsContent value="past" className="space-y-4">
                <div className="bg-electric/5 border border-electric/20 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 text-electric mb-2">
                    <Users className="w-5 h-5" />
                    <span className="font-medium">Your Event History</span>
                  </div>
                  <p className="text-white/60 text-sm">
                    Continue sharing memories and connecting with others who attended these events.
                  </p>
                </div>
                
                {pastEvents.length === 0 ? (
                  <div className="text-center py-16 border border-white/10 rounded-lg bg-obsidian-light">
                    <History className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50 mb-4">No past events yet</p>
                    <p className="text-white/40 text-sm">Events you RSVP to will appear here after they end</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {pastEvents.map((event) => (
                      <Link 
                        to={`/events/${event.id}`}
                        key={event.id}
                        className="block p-6 border border-white/10 rounded-lg bg-obsidian-light hover:border-amber-500/50 transition-colors"
                        data-testid={`past-event-${event.id}`}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-display text-xl text-white mb-1">{event.title}</h3>
                            <p className="text-amber-400 text-sm">{event.performer}</p>
                          </div>
                          <span className="flex items-center gap-1 text-amber-400 text-xs bg-amber-400/10 px-2 py-1 rounded">
                            <History className="w-3 h-3" />
                            Past Event
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-white/60">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(event.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {event.rsvp_count} attended
                          </span>
                        </div>
                        
                        <p className="text-electric text-sm mt-3">
                          View community posts →
                        </p>
                      </Link>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Posts Tab */}
              <TabsContent value="posts" className="space-y-4">
                {posts.length === 0 ? (
                  <div className="text-center py-16 border border-white/10 rounded-lg bg-obsidian-light">
                    <MessageSquare className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50 mb-4">You haven't posted anything yet</p>
                    <Link to="/events">
                      <Button variant="outline" className="border-electric text-electric hover:bg-electric/10">
                        Join the Conversation
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {posts.map(post => (
                      <div 
                        key={post.id}
                        className="p-6 border border-white/10 rounded-lg bg-obsidian-light"
                        data-testid={`user-post-${post.id}`}
                      >
                        <p className="text-white/80 mb-4">{post.content}</p>
                        <div className="flex items-center justify-between text-sm text-white/40">
                          <span>
                            {new Date(post.created_at).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </span>
                          <Link 
                            to={`/events/${post.event_id}`}
                            className="text-electric hover:underline"
                          >
                            View Event →
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
              
              {/* Settings Tab */}
              <TabsContent value="settings" className="space-y-6">
                <AccountSettings token={token} user={user} refreshUser={refreshUser} />
                <NotificationSettings token={token} />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </section>

      {/* Account Info */}
      <section className="py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl text-white mb-6">Account Information</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-6 border border-white/10 rounded-lg bg-obsidian-light">
              <p className="text-white/50 text-sm mb-1">Email</p>
              <p className="text-white">{user?.email}</p>
            </div>
            <div className="p-6 border border-white/10 rounded-lg bg-obsidian-light">
              <p className="text-white/50 text-sm mb-1">Member Since</p>
              <p className="text-white">
                {new Date(user?.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </p>
            </div>
            <div className="p-6 border border-white/10 rounded-lg bg-obsidian-light">
              <p className="text-white/50 text-sm mb-1">Membership Status</p>
              <p className={user?.is_subscriber ? "text-electric" : "text-white/60"}>
                {user?.is_subscriber ? `${(user.subscription_tier || 'Active').toUpperCase()} Member` : "Free Account"}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
